<script setup lang="ts">
import { computed, inject, onBeforeUnmount, onMounted, Ref, ref, watch } from 'vue';
import { useParticipantStore } from '@/Stores/ParticipantStore';
import { usePage } from '@inertiajs/vue3';
import { useRouter } from 'vue-router';
import { ErrRef } from '@/objects';
import Modal from '@/Components/Modal.vue';
import { useCopy, renderCopy } from '@/copy';
import { useSessionStore } from '@/Stores/AppStore';

const page = usePage()
const sessionData = page.props.chatSessionData
const config = page.props.experimentConfigData
const copy = useCopy()
const sessionStore = useSessionStore()
const pStore = useParticipantStore();
pStore.initialize();

const error = inject<ErrRef>("error")
const router = useRouter()
// Countdown badge next to the logout button (App.vue renders it); null hides it.
const actionDeadline = inject<Ref<number | null>>("action-deadline")

const disconnected = ref(false)

pStore.onError = (err) => {
    error.value = err
}

pStore.onDisconnected = () => {
    disconnected.value = true
    warningShown.value = false
    // Permanent exclusion is a terminal state, not an error: land on the dedicated page
    // (same treatment as Upcoming/NotAssigned; a reload gets there server-side too).
    router.push({ name: 'Disconnected', replace: true })
}

// Inactivity handling. The clock only runs while the participant can actually DO something
// (mirroring the server-side DisconnectionDeadlineService): it pauses while they wait on
// their partner, the consultant's reply or the other voters, and stops entirely once the
// game is over (feedback/questionnaire are untimed). Each stage has its own timeout: the
// very first action (instructions to read) and the inter-phase vote get the more generous
// ad-hoc values. There is deliberately no way to reset the clock other than actually
// progressing — the timeout bounds the synchronous session's length for everyone.
// Dismissing the warning modal only hides it (the dialog blocks the page, and the
// participant needs the page to act); it re-arms after the next unblock.
const warningShown = ref(false)
const warningDismissed = ref(false)
const idleSeconds = ref(0)

// "Ho capito": hide the warning until the next stage transition re-arms it (see the
// stateKey watch, which clears warningDismissed). The badge next to the logout button stays.
function dismissWarning() {
    warningDismissed.value = true
    warningShown.value = false
}
let idleInterval: ReturnType<typeof setInterval> | null = null

const warningMargin = Number(config["disconnectionWarning"] ?? 0)
const timeouts = {
    first: Number(config["firstActionTimeout"] ?? 0),
    round: Number(config["disconnectionTimeout"] ?? 0),
    vote: Number(config["voteTimeout"] ?? 0),
}

// The participant's current timer stage: which timeout applies, or why none does.
const stage = computed(() => {
    const s = pStore.state

    // Post-game stages (consultant feedback, questionnaire) are untimed, as is being out.
    if (sessionStore.paused || disconnected.value || s.phase2.completed || s.questionnaireCompleted) {
        return 'off'
    }

    // Inter-phase vote flow (rating, part-2 instructions, vote, beliefs). Keyed off
    // voteAvailable, not the route: the server starts the vote deadline the instant phase 1
    // completes, so the clock must run even while the participant is still on the result
    // board or the "wait for everyone" modal, before they navigate to the vote screen.
    if ((s.voteAvailable || s.phase1.completed) && !s.voteDone) {
        return 'vote'
    }

    const r = pStore.currentRound
    if (!r) {
        // Own rounds done but the phase (or the vote of others) isn't: waiting.
        return 'blocked'
    }
    if (r.choices[0]) {
        return 'blocked' // choice made, waiting for the partner
    }
    if (r.awaitingReply) {
        return 'blocked' // waiting for the consultant
    }

    // First-ever action of the session gets its own (longer) timeout.
    const untouchedFirst = pStore.currentPhase?.idx === 1 && r.idx === 0 && r.messages.length === 0
    return untouchedFirst ? 'first' : 'round'
})

const timerRunning = computed(() => stage.value in timeouts)
const stageTimeout = computed(() => timeouts[stage.value] ?? 0)

// Any transition while the timer runs re-anchors the clock: entering an actionable stage
// (unblocked by the partner/consultant/vote), moving to the next round, or a new message
// exchange. Own sends already re-anchor via markActivity() in the store.
const stateKey = computed(() =>
    `${stage.value}|${pStore.currentPhase?.idx}|${pStore.currentRound?.idx}|${pStore.currentRound?.messages.length}`
)

watch(stateKey, () => {
    if (timerRunning.value) {
        pStore.markActivity()
    }
    warningDismissed.value = false
    idleSeconds.value = 0
})

const remainingSeconds = computed(() => Math.max(0, Math.ceil(stageTimeout.value - idleSeconds.value)))

// mm:ss countdown to the permanent disconnection, shown big in the warning modal.
const countdown = computed(() => {
    const t = remainingSeconds.value
    const m = Math.floor(t / 60)
    const s = t % 60
    return [m, s].map((v) => String(v).padStart(2, "0")).join(":")
})

onMounted(() => {
    console.log(`Session data:`)
    console.log(sessionData)
    console.log(`Config:`)
    console.log(config)
    console.log("Store content:")
    console.log(pStore.state)
    console.log(`Current phase: ${pStore.currentPhase.idx}`)

    console.log(`Playing as participant: ${pStore.state.partId}`)

    idleInterval = setInterval(() => {
        if (warningMargin <= 0) {
            return
        }

        // The clock is paused (blocked/untimed): no idle accrues, no warning shows.
        if (!timerRunning.value || stageTimeout.value <= 0) {
            warningShown.value = false
            actionDeadline.value = null
            return
        }

        idleSeconds.value = (Date.now() - pStore.lastActivity) / 1000

        // Warn once fewer than `warningMargin` seconds remain before the kick. The
        // badge next to the logout button persists even after the modal is dismissed.
        const inWarning = remainingSeconds.value <= warningMargin
        actionDeadline.value = inWarning ? remainingSeconds.value : null

        if (inWarning && !warningDismissed.value) {
            warningShown.value = true
        }
    }, 1000)
})

onBeforeUnmount(() => {
    if (idleInterval) {
        clearInterval(idleInterval)
    }
    actionDeadline.value = null
})
</script>

<template>
    <template v-if="!sessionStore.paused">
        <Modal v-model="warningShown" variant="error" title="Sei ancora lì?"
            action-label="Ho capito" @action="dismissWarning">
            <div class="copy-html" v-html="renderCopy(copy('disconnect_warning'))"></div>
            <div class="col items-start gap-0.5 mt-3">
                <span class="text-gray-600 text-xs">tempo rimanente</span>
                <span class="font-mono font-bold text-lg" :class="remainingSeconds <= 30 ? 'text-error countdown-urgent' : ''">
                    {{ countdown }}
                </span>
            </div>
        </Modal>
    </template>
    <RouterView />
</template>

<style scoped>
/* Pulse the countdown once it enters the final stretch. */
.countdown-urgent {
    animation: countdown-pulse 1s ease-in-out infinite;
}

@keyframes countdown-pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.5; }
}
</style>
